<template>
  <div class="session-detail-page" v-loading="loading">
    <WelcomeBanner title="会话详情">
      <div slot="right">
        <el-button type="text" icon="el-icon-arrow-left" size="small" class="back-button" @click="goBack">返回</el-button>
      </div>
    </WelcomeBanner>

    <BaseCard>
      <el-row :gutter="20" class="detail-content">
      <el-col :span="16">
        <!-- 会话基本信息 -->
        <el-card class="info-card">
          <div slot="header" class="card-header">
            <span class="header-title">会话详情</span>
            <el-tag :type="getStatusTagType(session.status)" size="small">{{ getStatusText(session.status) }}</el-tag>
          </div>
          <div class="basic-info">
            <div class="session-type-badge">
              <el-tag effect="plain" size="medium">{{ getSessionTypeName(session.chat_type) }}</el-tag>
            </div>
            <h2 class="session-title">{{ session.content || '无标题会话' }}</h2>
            <div class="meta">
              <span><i class="el-icon-user"></i> 发送方：{{ session.sender_name || session.sender_id || '-' }}</span>
              <span><i class="el-icon-s-custom"></i> 接收方：{{ session.receiver_name || session.receiver_id || '-' }}</span>
              <span v-if="session.project_id"><i class="el-icon-folder"></i> 项目：{{ session.project_id }}</span>
            </div>
          </div>
        </el-card>

        <!-- 会话内容 -->
        <el-card class="content-card" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">会话内容</span>
          </div>
          <div class="content-box">{{ session.content || '暂无内容' }}</div>
        </el-card>

        <!-- 关联任务 -->
        <el-card class="related-tasks" style="margin-top: 20px;">
          <div slot="header" class="card-header">
            <span class="header-title">关联任务</span>
            <span class="task-count" v-if="relatedTasks.length">{{ relatedTasks.length }} 个任务</span>
          </div>
          <div v-if="relatedTasks.length">
            <el-table :data="relatedTasks" style="width: 100%" stripe>
              <el-table-column prop="task_name" label="任务名称" min-width="200" show-overflow-tooltip />
              <el-table-column prop="status" label="状态" width="90" align="center">
                <template slot-scope="scope">
                  <el-tag :type="getStatusTagType(scope.row.status)" size="small">{{ getStatusText(scope.row.status) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="receiver_id" label="执行人" width="120" show-overflow-tooltip />
              <el-table-column prop="finish_time" label="截止时间" width="140">
                <template slot-scope="scope">{{ formatTime(scope.row.finish_time) }}</template>
              </el-table-column>
              <el-table-column label="操作" width="90" align="center">
                <template slot-scope="scope">
                  <el-button type="text" size="small" @click="goToTaskDetail(scope.row.task_id)">查看</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
          <div v-else class="empty-state">
            <i class="el-icon-document" style="font-size: 48px; color: #dcdfe6;"></i>
            <p>暂无关联任务</p>
          </div>
        </el-card>
      </el-col>

      <el-col :span="8">
        <!-- 会话信息 -->
        <el-card class="info-sidebar">
          <div slot="header" class="card-header">
            <span class="header-title">会话信息</span>
          </div>
          <div class="info-list">
            <div class="info-item">
              <span class="label">会话ID</span>
              <span class="value code">{{ session.session_id }}</span>
            </div>
            <div class="info-item">
              <span class="label">创建时间</span>
              <span class="value">{{ formatTime(session.create_time) }}</span>
            </div>
            <div class="info-item">
              <span class="label">截止时间</span>
              <span class="value">{{ formatTime(session.due_time) || '未设置' }}</span>
            </div>
            <div class="info-item">
              <span class="label">发送方</span>
              <span class="value">{{ session.sender_name || session.sender_id || '-' }}</span>
            </div>
            <div class="info-item">
              <span class="label">接收方</span>
              <span class="value">{{ session.receiver_name || session.receiver_id || '-' }}</span>
            </div>
            <div class="info-item" v-if="session.task_id">
              <span class="label">所属任务</span>
              <span class="value code">{{ session.task_id }}</span>
            </div>
            <div class="info-item">
              <span class="label">类型</span>
              <span class="value">{{ getSessionTypeName(session.chat_type) }}</span>
            </div>
            <div class="info-item">
              <span class="label">状态</span>
              <span class="value">
                <el-tag :type="getStatusTagType(session.status)" size="mini">{{ getStatusText(session.status) }}</el-tag>
              </span>
            </div>
            <div class="info-item" v-if="session.confirm_standard">
              <span class="label">反馈确认标准</span>
              <span class="value">{{ session.confirm_standard }}</span>
            </div>
          </div>
        </el-card>

        <!-- 操作按钮 -->
        <el-card class="action-card" style="margin-top: 20px;">
          <div class="action-buttons">
            <el-button type="primary" icon="el-icon-edit" style="width: 100%; margin-bottom: 12px;" @click="editSession">编辑会话</el-button>
            <el-button type="danger" icon="el-icon-delete" plain style="width: 100%;" @click="deleteSession">删除会话</el-button>
          </div>
        </el-card>
      </el-col>
    </el-row>
    </BaseCard>
  </div>
</template>

<script>
import { getSessionDetail, deleteSession } from '../api/session'
import { getTaskDetail } from '../api/task'
import BaseCard from '@/components/BaseCard'
import WelcomeBanner from '@/components/WelcomeBanner'

export default {
  name: 'SessionDetail',
  components: { BaseCard, WelcomeBanner },
  data() {
    return {
      sessionId: null,
      session: {},
      relatedTasks: [],
      loading: false
    }
  },
  created() {
    this.sessionId = this.$route.params.id
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        // 加载会话详情 - 拦截器已返回 res.data
        this.session = await getSessionDetail(this.sessionId) || {}

        // 如果会话有关联的任务ID，加载任务详情
        if (this.session.task_id) {
          try {
            const task = await getTaskDetail(this.session.task_id)
            if (task) {
              this.relatedTasks = [task]
            }
          } catch (taskError) {
            console.error('加载关联任务失败:', taskError)
            this.relatedTasks = []
          }
        } else {
          this.relatedTasks = []
        }
      } catch (error) {
        console.error('加载会话详情失败:', error)
        this.$message.error(`加载失败: ${error.message || '未知错误'}`)
      } finally {
        this.loading = false
      }
    },
    getSessionTypeName(type) {
      const typeMap = {
        '1': '执行会话',
        '2': '协调会话',
        '3': '反馈会话',
        '4': '其他'
      }
      return typeMap[type] || type
    },
    getStatusText(status) {
      const statusMap = {
        '1': '待处理',
        '2': '处理中',
        '3': '已处理',
        '4': '已过期'
      }
      return statusMap[status] || status
    },
    getStatusTagType(status) {
      const typeMap = {
        '1': 'warning',
        '2': 'primary',
        '3': 'success',
        '4': 'danger'
      }
      return typeMap[status] || 'info'
    },
    formatTime(time) {
      if (!time) return ''
      return new Date(time).toLocaleString('zh-CN')
    },
    goBack() {
      this.$router.push('/sessions')
    },
    goToTaskDetail(taskId) {
      this.$router.push(`/task/${taskId}`)
    },
    editSession() {
      this.$router.push(`/sessions/edit/${this.sessionId}`)
    },
    async deleteSession() {
      try {
        await this.$confirm('确定要删除该会话吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await deleteSession(this.sessionId)
        this.$message.success('会话删除成功')
        this.$router.push('/sessions')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除会话失败:', error)
          this.$message.error(`删除失败: ${error.message || '未知错误'}`)
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.session-detail-page {
  padding: 20px;
  min-height: calc(100vh - 84px);
  background-color: var(--bg-page);
}

.page-header {
  margin-bottom: 20px;
}

.detail-content {
  margin: 0 !important;
}

/* 卡片通用样式 */
.info-card,
.content-card,
.related-tasks,
.info-sidebar,
.action-card {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.task-count {
  font-size: 13px;
  color: var(--text-muted);
}

/* 会话基本信息 */
.basic-info {
  padding: 8px 0;

  .session-type-badge {
    margin-bottom: 12px;
  }

  .session-title {
    margin: 0 0 16px 0;
    font-size: 20px;
    color: var(--text-primary);
    line-height: 1.4;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    color: var(--text-muted);
    font-size: 13px;

    span {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    i {
      font-size: 14px;
    }
  }
}

/* 会话内容 */
.content-box {
  background-color: var(--bg-page);
  padding: 20px;
  border-radius: var(--radius-lg);
  line-height: 1.8;
  color: var(--text-secondary);
  font-size: 14px;
  white-space: pre-line;
  min-height: 100px;
}

/* 会话信息侧边栏 */
.info-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;

  .label {
    font-size: 12px;
    color: var(--text-muted);
  }

  .value {
    font-size: 14px;
    color: var(--text-primary);
    word-break: break-all;

    &.code {
      font-family: monospace;
      font-size: 12px;
      color: var(--text-secondary);
      background-color: var(--bg-page);
      padding: 4px 8px;
      border-radius: var(--radius-sm);
    }
  }
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  flex-direction: column;
}

/* 关联任务 */
.related-tasks {
  margin-top: 20px;
}

/* 空状态 */
.empty-state {
  text-align: center;
  color: var(--text-disabled);
  padding: 40px 20px;

  p {
    margin-top: 12px;
    font-size: 14px;
  }
}
</style>
